import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useMnemonicScopeId } from '@/contexts/MnemonicContext';
import { getVersion, isEffective, register, subscribe } from '@/lib/mnemonics/registry';

/**
 * Bind Alt+<char> to `handler` within the nearest scope.
 *
 * Returns whether the binding currently wins resolution, so a control can hide
 * its underline when something with higher precedence has taken the character.
 */
export function useMnemonic(char, handler, { label = '', disabled = false } = {}) {
  const scopeId = useMnemonicScopeId();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!scopeId || !char) return undefined;
    return register(scopeId, char, {
      label,
      disabled,
      handler: (event) => handlerRef.current?.(event),
    });
  }, [scopeId, char, label, disabled]);

  useSyncExternalStore(subscribe, getVersion, getVersion);
  return !!scopeId && !disabled && isEffective(scopeId, char);
}

export default useMnemonic;
